import {Chat, InjectSearchOverlay} from '@components';
import {metaInfo} from '@configs';
import {CATEGORY_ICONS_LINKS, COURSE, HOME, LEARNING} from '@constants';
import {useValidateAuth} from '@jobseeker/common-core';
import {Interceptor} from '@jobseeker/common-core/components';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {courseService} from '@jobseeker/common-core/services';
import {wrapper} from '@jobseeker/common-core/stores';
import {AppState} from '@jobseeker/common-core/types';
import {InitiateUBA, TrackEventUBA} from '@jobseeker/common-core/utils';
import {categories} from '@jobseeker/feature-course/constants';
import {Loader} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {cleanUrl, isCSR} from '@jobseeker/lotus/utils';
import {NavLayout, ResponsiveLayout} from '@layouts';
import {SeoIntrup} from '@layouts/SeoLayout';
import {gladiatorService} from '@jobseeker/common-core/services';
import {Box, Card, Grid, Typography, useTheme} from '@mui/material';
import {
  getConfigByApp,
  getProfileSchema,
  utilServerSideDeviceDetection,
} from '@utils';
import cookie from 'cookie';
import type {GetServerSidePropsContext} from 'next/types';
import dynamic from 'next/dynamic';
import {Image} from '@jobseeker/lotus/components/Image';
import {useRouter} from 'next/router';
import {memo, useEffect, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME;
const isAppIimjobs = PLATFORM === 'iimjobs';
const isAppHirist = PLATFORM === 'hirist';

const CourseList = dynamic(
  () => import('@jobseeker/lotus/components/JobList/V2')
);

const HiristCourseFeed = dynamic(
  () => import('@jobseeker/feature-course/components/HiristCourseFeed')
);

const AdsPanelHarness = memo(
  dynamic(
    () => import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
    {
      ssr: false,
    }
  )
);

const LoginForm = dynamic(
  () =>
    isAppHirist
      ? import(
          '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
        )
      : Promise.reject(''),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

type LearningCategoryProps = {
  userIdSSR: number;
  categoryId: number;
  categoryCourses: Course[];
};

type Course = {
  id: number;
  title: string;
  location?: Array<{id: number; name: string}>;
  url: string;
  logo: string;
  institute: string;
  category: Array<{id: number; name: string}>;
};

const CategoryCoursesPage = ({
  userIdSSR,
  categoryId,
  categoryCourses,
}: LearningCategoryProps) => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  useValidateAuth(dispatch);

  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );

  const [loginOpen, setLoginOpen] = useState(false);

  const handleEnquiryInLoggedOutState = () => {
    setLoginOpen(true);
  };

  const isDeviceLarge = useIsLargeDevice('md');
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);
  const router = useRouter();
  useEffect(() => {
    InitiateUBA('L&G Category Course Listing');

    const sessionUserId = userIdSSR || userId || '-1';

    TrackEventUBA('lgCategoryCoursesPageView', 'view', {
      categoryID: categoryId || '',
      ref: router.query.ref || '',
      sessionUsername: sessionUserId,
    });
  }, []);

  const categoryName = isAppIimjobs
    ? categoryCourses?.[0]?.category?.find(cat => cat.id == categoryId)?.name ??
      ''
    : categories.find(d => d.id === categoryId)?.name;

  const metadata = getConfigByApp(metaInfo, HOME, true, LEARNING);

  return (
    <>
      <Interceptor metaInfo={metadata}>
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={[]}
          >
            {isCSR() && (
              <SeoIntrup
                title="Course List"
                description={
                  'Courses for working professionals and entrepreneurs to learn & develop advanced leadership & management skills.'
                }
                pageNo={0}
                nextPage={false}
                type={COURSE}
              />
            )}
            <Grid container columnSpacing={2}>
              <Grid item xxs={12} md={isAppIimjobs ? 9 : 12}>
                <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
                {categoryCourses.length > 0 ? (
                  <Box>
                    <Card
                      sx={{
                        boxShadow: 2,
                        borderRadius: 4,
                        mx: 0,
                        mt: 2,
                        pt: 2,
                        ...(isAppHirist && {
                          bgcolor: 'transparent',
                          boxShadow: 'none',
                          pr: {xxs: 0, md: 4},
                          pl: {xxs: 0, md: 2},
                          maxWidth: 1056,
                        }),
                      }}
                    >
                      {isAppIimjobs && (
                        <Typography
                          component="h3"
                          fontSize={theme.typography.pxToRem(20)}
                          fontWeight="bold"
                          pl={2}
                          mb={1}
                        >
                          {categoryName}
                        </Typography>
                      )}
                      {isAppIimjobs &&
                        categoryCourses.map(
                          ({institute, title, location, id, logo}) => (
                            <CourseList
                              key={id}
                              title={title}
                              variant={VersionVariants.VERSION_TWO}
                              location={
                                location && location?.length > 3
                                  ? 'Multiple Locations'
                                  : location &&
                                    location?.map(l => l.name).join('/')
                                      .length > 30
                                  ? 'Multiple Locations'
                                  : location?.map(l => l.name).join('/') ||
                                    'Anywhere'
                              }
                              companyName={institute}
                              experiance={institute}
                              date={Date.now()}
                              refCode=""
                              companyImgUrl={logo}
                              showPosted={false}
                              isCompanyJob={false}
                              isPremiumJob={false}
                              isSaved={false}
                              showSaveIcon={false}
                              redirectionLink={
                                new URL(
                                  `/course/${cleanUrl(
                                    title
                                  )}-${id}?reforg=learningcat_${categoryId}&refinv=learning`,
                                  process.env.NEXT_PUBLIC_LOAD_CONFIG
                                ).href
                              }
                              whatFor="course"
                            />
                          )
                        )}
                      {isAppHirist && (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            mb={isAppHirist ? 2 : 0}
                          >
                            {isAppHirist && (
                              <Image
                                src={CATEGORY_ICONS_LINKS[categoryId]}
                                alt={categoryName ?? ''}
                                height={35}
                                style={{
                                  marginLeft: '16px',
                                  width: 'auto',
                                }}
                              />
                            )}
                            <Typography
                              component="h3"
                              fontSize={theme.typography.pxToRem(20)}
                              fontWeight="bold"
                              pl={2}
                              mb={isAppIimjobs ? 1 : 0}
                            >
                              {categoryName}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {isAppHirist && (
                        <HiristCourseFeed
                          title=" "
                          titleProps={{fontWeight: 700}}
                          showFiltersAndViewAllCoursesText={false}
                          showAllCourses
                          coursesArr={categoryCourses}
                          handleEnquiryInLoggedOutState={
                            handleEnquiryInLoggedOutState
                          }
                          sx={{
                            maxWidth: 1024,
                          }}
                        />
                      )}
                    </Card>
                  </Box>
                ) : (
                  <Typography>No courses found for this category.</Typography>
                )}
              </Grid>
              {isDeviceLarge && isAppIimjobs && (
                <Grid item xxs={12} md={3}>
                  <AdsPanelHarness
                    variant={VersionVariants.VERSION_TWO}
                    showSmallBadges={false}
                    showLearning
                    showFeatured={false}
                    showLearningCenterCTA={false}
                    showLearnGrow={false}
                  />
                </Grid>
              )}
            </Grid>
            {isAppHirist && loginOpen && (
              <LoginForm
                variant={VersionVariants.VERSION_TWO}
                openDialog={loginOpen}
                setOpenDialog={() => {
                  setLoginOpen(false);
                }}
                onSuccess={() => window.location.reload()}
              />
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    let userId = -1;

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '0', 10);
        }
      }
    } catch (err) {}

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: LEARNING},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'AppContext/set_enable_ads_preload',
        payload: true,
      });
    }

    store.dispatch({type: 'job/jobdetail/set_initial_state'});

    const {slug = ''} = context.query;

    let categoryId = 0;

    try {
      categoryId = parseInt(
        String(slug)
          .replace(/\.html/, '')
          .split('-')
          .pop() ?? '0',
        10
      );
    } catch (err) {
      categoryId = 0;
    }

    if (Number.isNaN(categoryId) || categoryId === 0) {
      return {
        notFound: true,
      };
    }

    const categoryCourses: Course[] =
      (await courseService.getCategoryCourses({categoryId}))?.CourseList ?? [];

    try {
      const schema: {[key: string]: string} = await getProfileSchema();
      store.dispatch({
        type: 'User/set_profile_schema',
        payload: schema,
      });
    } catch (err) {}

    return {
      props: {
        userIdSSR: userId,
        categoryCourses,
        categoryId,
      },
    };
  }
);

export default CategoryCoursesPage;
